import React, { useEffect, useState } from 'react';
import OptimizedImage from './OptimizedImage';
import UnsplashImage from './UnsplashImage';
import NewsletterForm from './NewsletterForm';
import { useCodeCopyTracking, useScrollDepthTracking, useTimeOnPageTracking } from '../utils/analytics';
import { getRelatedPosts } from '../utils/recommendations';
import { Link, useNavigate } from 'react-router-dom';
import { BlogPostMetadata, getBlogPostContent } from '../data/blogData';
import MarkdownRenderer from './MarkdownRenderer';
import ErrorBoundary from './ErrorBoundary';

interface BlogPostProps {
  post: BlogPostMetadata;
  isPreview?: boolean;
  allPosts?: BlogPostMetadata[];
}

const BlogPost: React.FC<BlogPostProps> = ({ post, isPreview = false, allPosts = [] }) => {
  const { title, date, author, tags, image, excerpt, slug, contentPath, unsplashQuery } = post;
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(!isPreview);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostMetadata[]>([]);
  const navigate = useNavigate();
  
  // Use analytics hooks
  useCodeCopyTracking();
  useScrollDepthTracking();
  useTimeOnPageTracking();
  
  // Load blog content
  useEffect(() => {
    if (!isPreview && slug) {
      const loadContent = async () => {
        try {
          setIsLoading(true);
          
          // For direct debugging in the browser console
          console.log(`Loading blog post with slug: ${slug}`);
          console.log(`Content path from metadata: ${contentPath}`);
          
          // Use the getBlogPostContent function from blogData.ts
          const rawMarkdown = await getBlogPostContent(slug);
          
          if (!rawMarkdown || rawMarkdown.length < 10) {
            throw new Error(`Failed to load content for ${slug} - content too short or empty`);
          }
          
          console.log(`Successfully loaded content for ${slug}, length: ${rawMarkdown.length} characters`);
          
          // Store the raw markdown - we'll process it with MarkdownRenderer
          setContent(rawMarkdown);
        } catch (error) {
          console.error('Error loading blog content:', error);
          setContent(`# Error Loading Content\n\nSorry, we couldn't load the content for this blog post. Please try again later.\n\nError details: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
          setIsLoading(false);
        }
      };
      
      loadContent();
    }
  }, [slug, contentPath, isPreview]);
  
  // Load related posts
  useEffect(() => {
    if (!isPreview && allPosts.length > 0) {
      const loadRelatedPosts = async () => {
        const related = await getRelatedPosts(post, allPosts);
        setRelatedPosts(related);
      };
      
      loadRelatedPosts();
    }
  }, [post, allPosts, isPreview]);
  
  // Format the date
  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Handle direct navigation to the blog post
  const handleReadMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log(`Navigating to blog post: /blog/${slug}`);
    navigate(`/blog/${slug}`);
  };

  // Determine the query to use for Unsplash
  const getUnsplashQuery = () => {
    // Use explicit unsplashQuery if provided, otherwise generate from tags and title
    return unsplashQuery || `${tags.join(' ')} ${title}`;
  };

  if (isPreview) {
    return (
      <div className="w-full rounded-lg overflow-hidden shadow-lg bg-white dark:bg-gray-800 hover:shadow-xl transition-shadow duration-300">
        <div className="relative h-48 w-full overflow-hidden">
          <ErrorBoundary>
            {image && image.trim() !== "" ? (
              <OptimizedImage
                src={image}
                alt={title}
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <UnsplashImage
                query={getUnsplashQuery()}
                alt={title}
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                height="h-48"
              />
            )}
          </ErrorBoundary>
        </div>
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">{title}</h2>
          <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
            {formattedDate} • {author}
          </p>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{excerpt}</p>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag: string) => (
              <span
                key={tag}
                className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 text-xs font-medium rounded"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="read-more-link mt-4">
            <button
              onClick={handleReadMoreClick}
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium transition-colors no-underline"
            >
              Read more
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
      <header className="p-6 md:p-8">
        <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-white">{title}</h1>
        <p className="text-gray-700 dark:text-gray-300">
          {formattedDate} • {author}
        </p>
        <ErrorBoundary>
          <div className="mt-6">
            {image && image.trim() !== "" ? (
              <OptimizedImage 
                src={image} 
                alt={title} 
                className="w-full h-auto rounded-lg shadow-md" 
              />
            ) : (
              <UnsplashImage
                query={getUnsplashQuery()}
                alt={title}
                className="w-full h-auto rounded-lg shadow-md"
                height="h-64"
              />
            )}
          </div>
        </ErrorBoundary>
        <div className="mt-4 flex flex-wrap gap-2">
          {tags.map((tag: string) => (
            <span
              key={tag}
              className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 text-sm font-medium rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      </header>

      <div className="p-6 md:p-8">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-400">Loading content...</span>
          </div>
        ) : (
          <div className="blog-content">
            {content && <MarkdownRenderer content={content} />}
          </div>
        )}
      </div>
      
      {/* Newsletter subscription */}
      <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700">
        <NewsletterForm />
      </div>
      
      {/* Related posts section */}
      {relatedPosts.length > 0 && (
        <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            Related Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relatedPosts.map(post => (
              <div key={post.slug} className="group">
                <Link to={`/blog/${post.slug}`} className="no-underline">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {post.excerpt.length > 120 ? `${post.excerpt.substring(0, 120)}...` : post.excerpt}
                  </p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default BlogPost;
